import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, getUser, clearAuth, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Building2, FileText, LogOut, Save, Loader2, Download, Send } from "lucide-react";

export default function CorporatePortal() {
  const user = getUser();
  const [me, setMe] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [billing, setBilling] = useState("");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const [m, inv] = await Promise.all([api.get("/corporate/me"), api.get("/corporate/me/invoices")]);
      setMe(m.data); setInvoices(inv.data); setBilling(m.data.corporate?.billing_email || "");
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { if (user?.role === "corporate_admin") load(); }, []); // eslint-disable-line

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "corporate_admin") return <Navigate to="/" replace />;

  const logout = () => { clearAuth(); window.location.replace("/login"); };

  const saveBilling = async () => {
    setSaving(true);
    try {
      await api.patch("/corporate/me", { billing_email: billing });
      toast.success("Billing email updated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const generateInvoice = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/corporate/me/invoice/generate");
      toast.success(`Invoice emailed — ${data.trips} trip(s), NZ$ ${data.total_nzd.toFixed(2)}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setGenerating(false); }
  };

  const downloadPdf = async (invId) => {
    try {
      const token = localStorage.getItem("apex_token");
      const res = await fetch(`${BACKEND_URL}/api/corporate/me/invoices/${invId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `invoice-${invId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const corp = me?.corporate;

  return (
    <div className="min-h-screen bg-[#090D14] text-white">
      <header className="fixed top-0 inset-x-0 z-40 border-b border-white/10 bg-[#090D14]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center text-[#090D14] font-black">T</div>
            <div className="leading-tight">
              <div className="font-display font-extrabold">Corporate Portal</div>
              <div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-mono">Triptap Taxi</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono text-slate-400">{user.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="corp-logout" className="text-slate-300 hover:text-red-400"><LogOut className="w-4 h-4 mr-2" /> Sign out</Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 pt-28 pb-16">
        <div className="text-caption mb-2">Business account</div>
        <h1 className="font-display text-4xl font-extrabold flex items-center gap-3"><Building2 className="w-8 h-8 text-amber-400" /> {corp?.name || "Loading…"}</h1>
        <p className="text-slate-400 mt-2">Manage your billing email, generate a fresh invoice, and download past PDFs.</p>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Stat label="Riders on your domain" value={me?.rider_count ?? "—"} sub={`@${corp?.email_domain || "—"}`} />
          <Stat label="Completed trips (lifetime)" value={me?.completed_trips ?? "—"} />
          <Stat label="Monthly limit" value={`NZ$ ${Number(corp?.monthly_limit_nzd || 0).toFixed(0)}`} />
        </div>

        <div className="mt-8 glass-card rounded-2xl p-6 gold-border">
          <h2 className="font-display text-lg font-bold">Billing email</h2>
          <p className="text-sm text-slate-400 mt-1">All PDF invoices are delivered here.</p>
          <div className="mt-4 flex gap-2 max-w-xl">
            <Input value={billing} onChange={(e) => setBilling(e.target.value)} placeholder="accounts@company.co.nz" className="bg-black/30 border-white/10 h-11" data-testid="corp-billing-input" />
            <Button onClick={saveBilling} disabled={saving} className="btn-gold" data-testid="corp-billing-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}</Button>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-amber-400" /> Invoices</h2>
          <Button onClick={generateInvoice} disabled={generating} className="btn-gold rounded-full" data-testid="corp-generate-invoice">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Generate & email now
          </Button>
        </div>

        <div className="mt-4 space-y-2">
          {invoices.length === 0 && <div className="glass-card rounded-2xl p-8 text-center text-slate-500 gold-border">No invoices yet. Click "Generate & email now" for the current period.</div>}
          {invoices.map((inv) => (
            <div key={inv.id} className="glass-card rounded-xl p-4 gold-border flex flex-wrap items-center justify-between gap-3" data-testid={`corp-inv-${inv.id}`}>
              <div>
                <div className="font-mono text-amber-400 font-bold">{inv.period_label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{new Date(inv.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-3">
                <Badge className="bg-white/5 border-white/10 text-slate-300 font-mono text-xs">{inv.trips} trip{inv.trips !== 1 ? "s" : ""}</Badge>
                <div className="font-mono text-lg text-white">NZ$ {Number(inv.total_nzd).toFixed(2)}</div>
                <Button size="sm" variant="outline" onClick={() => downloadPdf(inv.id)} className="bg-transparent border-amber-400/40 text-amber-400 hover:bg-amber-400/10" data-testid={`corp-inv-download-${inv.id}`}>
                  <Download className="w-3.5 h-3.5 mr-2" /> PDF
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, sub }) => (
  <div className="glass-card rounded-2xl p-5 gold-border">
    <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-display text-3xl font-extrabold mt-1">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5 font-mono">{sub}</div>}
  </div>
);
